/**
 * 네트워크 호출 차단 — **태그가 아니라 코드로 나가는 것**을 막는다.
 *
 * ## 왜 따로 필요한가
 *
 * `prior-blocking.ts` 는 `src`·`href` 속성을 가진 **요소**를 다룬다. 그런데
 * 요즘 추적기는 대개 **번들에 들어와 `fetch`·`sendBeacon` 으로 보낸다.**
 * 태그가 없으니 속성을 뗄 것도 없다.
 *
 * 실제로 걸렸다 — 소비 제품의 오류 추적기가 `<script src>` 가 아니라
 * 번들 안에서 초기화되고 `fetch` 로 전송한다. **호스트 목록에 올려도
 * 막히지 않는 상태**였고, 그 사실을 모른 채 「목록에 있으니 막힌다」고
 * 답할 뻔했다.
 *
 * ## 이것은 **안전망**이지 주 수단이 아니다
 *
 * 주 수단은 **소비 측이 동의 상태를 보고 호출을 미루는 것**이다. 초기화
 * 자체를 늦춰야 전역 오류 처리기 설치 같은 부수 효과도 안 일어난다.
 * 네트워크만 막으면 **라이브러리는 「보냈다」고 믿는 상태**가 된다.
 *
 * 그래도 둔다. 코드 게이팅에 구멍이 생겨도 **데이터는 안 나간다.**
 */

const BLOCKED = new Set<string>();

function hostOf(url: string): string | null {
  try {
    return new URL(url).hostname;
  } catch {
    /* 절대 URL 이 아니다 — 같은 출처다 */
  }
  const base =
    typeof location !== 'undefined' && typeof location.href === 'string' ? location.href : null;
  if (base === null) return null;
  try {
    return new URL(url, base).hostname;
  } catch {
    return null;
  }
}

function matches(url: string): boolean {
  const h = hostOf(url);
  if (h === null) return false;
  for (const b of BLOCKED) {
    if (h === b || h.endsWith(`.${b}`)) return true;
  }
  return false;
}

/** 지금까지 막은 호출. 진단용이다 — **조용히 막으면 디버깅이 지옥이 된다.** */
export const blockedCalls: string[] = [];

function note(url: string): void {
  blockedCalls.push(url);
  // 막았다는 것을 **알린다.** 사이트 개발자가 「왜 안 가지」로 몇 시간을
  // 쓰지 않게 한다. 경고 수준은 과하지 않다 — 동의 전에는 정상 동작이다.
  if (typeof console !== 'undefined') {
    console.info(`[ray-cookie-banner] 동의 전이라 보내지 않음: ${url}`);
  }
}

/**
 * 차단을 켠다. 되돌리는 함수를 돌려준다.
 *
 * `fetch` 는 **거부가 아니라 빈 성공**으로 답한다. 거부하면 호출한 쪽이
 * 오류 처리를 타는데, 그게 또 오류 보고를 부르는 고리가 생길 수 있다.
 * 조용히 성공시키는 쪽이 부작용이 적다.
 */
/**
 * 우리가 올린 패치. **가드는 이것과 비교한다** — 비교 대상과 설치 대상이
 * 다르면 가드가 늘 통과해 패치가 겹쳐 쌓인다.
 */
const installed = new Map<string, unknown>();

export function startNetBlocking(hosts: readonly string[]): () => void {
  // 호스트는 매번 더한다 — 두 번째 호출이 대상만 넓히는 경우가 있다.
  for (const h of hosts) BLOCKED.add(h);
  if (hosts.length === 0 && installed.size === 0) return () => undefined;

  const undo: Array<() => void> = [];

  if (typeof globalThis.fetch === 'function' && installed.get('fetch') !== globalThis.fetch) {
    const original = globalThis.fetch;
    globalThis.fetch = function patched(this: unknown, input: RequestInfo | URL, init?: RequestInit) {
      const url =
        typeof input === 'string'
          ? input
          : input instanceof URL
            ? input.href
            : (input as Request).url;
      if (matches(url)) {
        note(url);
        return Promise.resolve(new Response(null, { status: 204 }));
      }
      return original.call(this, input, init);
    } as typeof fetch;
    installed.set('fetch', globalThis.fetch);
    undo.push(() => {
      globalThis.fetch = original;
      installed.delete('fetch');
    });
  }

  if (typeof XMLHttpRequest === 'function' && installed.get('xhr') !== XMLHttpRequest.prototype.open) {
    const original = XMLHttpRequest.prototype.open;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (XMLHttpRequest.prototype as any).open = function patched(
      this: XMLHttpRequest,
      method: string,
      url: string | URL,
      ...rest: unknown[]
    ) {
      const s = typeof url === 'string' ? url : url.href;
      // 막을 때는 **빈 데이터 URL 로 돌린다.** 던지면 호출한 쪽이 깨지고,
      // 그 오류가 또 오류 보고를 부르는 고리가 생길 수 있다.
      const target = matches(s) ? (note(s), 'data:,') : url;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (original as any).call(this, method, target, ...rest);
    };
    installed.set('xhr', XMLHttpRequest.prototype.open);
    undo.push(() => {
      XMLHttpRequest.prototype.open = original;
      installed.delete('xhr');
    });
  }

  if (
    typeof navigator !== 'undefined' &&
    typeof navigator.sendBeacon === 'function' &&
    installed.get('beacon') !== navigator.sendBeacon
  ) {
    const original = navigator.sendBeacon.bind(navigator);
    navigator.sendBeacon = function patched(url: string | URL, data?: BodyInit | null) {
      const s = typeof url === 'string' ? url : url.href;
      if (matches(s)) {
        note(s);
        // **`true` 를 돌려준다.** `false` 면 호출한 쪽이 재시도하는 구현이 있다.
        return true;
      }
      return original(url, data);
    };
    installed.set('beacon', navigator.sendBeacon);
    undo.push(() => {
      navigator.sendBeacon = original;
      installed.delete('beacon');
    });
  }

  return () => {
    BLOCKED.clear();
    for (const fn of undo) fn();
  };
}

/** 허용된 호스트를 차단 목록에서 뺀다. 동의 후에 부른다. */
export function allowHosts(hosts: readonly string[]): void {
  for (const h of hosts) BLOCKED.delete(h);
}
